<script setup>
const props = defineProps({
  agent: { type: Object, required: true },
  accountId: { type: [String, Number], required: true },
  commercial: { type: Boolean, default: true },
  nowSec: { type: Number, required: true },
  serverTime: { type: Number, default: 0 },
  isExpanded: { type: Boolean, default: false },
  isExpandedRecv: { type: Boolean, default: false },
  sfIsOpen: { type: Boolean, default: false },
  sfDraftValue: { type: String, default: '' },
  sfIsBusy: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggle-expand',
  'toggle-expand-recv',
  'open-received-modal',
  'toggle-rodizio',
  'toggle-sf',
  'update-sf-draft',
  'save-salesforce-id',
]);

const STATUS_COLOR = { active: '#10b981', idle: '#f59e0b', offline: '#9ca3af' };
const statusColor = status => STATUS_COLOR[status] || STATUS_COLOR.offline;

// Aceleres: cor dedicada para ausência do dia (falta/férias/folga marcada no
// calendário) — o badge de status vira o motivo em vez do online/offline.
const ABSENCE_COLOR = '#6366f1';
const AVAILABILITY_COLOR = '#dc2626';
const NOTICE_COLOR = '#d97706';

const initials = name =>
  (name || '?').trim().split(/\s+/).slice(0, 2).map(p => p[0] || '').join('').toUpperCase();

const formatAgo = epochSeconds => {
  if (!epochSeconds) return '';
  const ref = props.serverTime || Math.floor(Date.now() / 1000);
  const diff = Math.max(0, ref - epochSeconds);
  if (diff < 60) return 'há instantes';
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `há ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `há ${hrs} h`;
  return `há ${Math.floor(hrs / 24)} d`;
};

const formatDateTime = epochSeconds => {
  if (!epochSeconds) return '';
  const d = new Date(epochSeconds * 1000);
  const now = new Date();
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (sameDay(d, now)) return `hoje às ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return `ontem às ${time}`;
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${time}`;
};

const subtitleFor = agent => {
  if (agent.status === 'active') return 'Ativo agora';
  if (agent.status === 'idle') return `Ocioso ${formatAgo(agent.last_activity_at)}`;
  if (agent.last_seen_at) return `Visto ${formatDateTime(agent.last_seen_at)}`;
  return 'Sem conexão';
};

// Aceleres: quando o vendedor está ausente hoje (falta/férias/folga marcada no
// calendário), o badge de status vira o motivo (cor dedicada). Senão, status normal.
const isRodizioEffective = agent =>
  agent.rodizio_effective_enabled ?? agent.rodizio_enabled;
const isAutomaticRodizioBlock = agent =>
  agent.rodizio_enabled !== false && agent.rodizio_effective_enabled === false;
const rodizioToggleTitle = agent => {
  if (isAutomaticRodizioBlock(agent)) {
    return `${agent.availability_label || 'Indisponível'} — bloqueado automaticamente`;
  }

  return agent.rodizio_enabled ? 'Tirar do rodízio' : 'Colocar no rodízio';
};
const cardColor = agent => {
  if (!isRodizioEffective(agent)) return agent.absente ? ABSENCE_COLOR : AVAILABILITY_COLOR;
  if (agent.availability_notice) return NOTICE_COLOR;
  return statusColor(agent.status);
};
const cardLabel = agent =>
  !isRodizioEffective(agent)
    ? agent.availability_label || agent.absente_label || 'Fora do rodízio'
    : agent.availability_notice || subtitleFor(agent);

const pendingOf = agent => agent.pending_leads || [];
const remainingFor = lead => lead.deadline_at - props.nowSec;

const fmtCountdown = secs => {
  const s = Math.max(0, secs);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

const urgencyColor = secs => {
  if (secs <= 0) return '#dc2626';
  if (secs <= 60) return '#ef4444';
  if (secs <= 120) return '#f59e0b';
  return '#10b981';
};

const topLead = agent => pendingOf(agent)[0] || null;

const badgeColor = agent => {
  const lead = topLead(agent);
  if (!props.commercial || !lead || !lead.circulating) return '#64748b';
  return urgencyColor(remainingFor(lead));
};

const vencidosOf = agent => (agent.lost_leads || []).length;
const convUrl = displayId => `/app/accounts/${props.accountId}/conversations/${displayId}`;

const fmtHora = epoch =>
  epoch
    ? new Date(epoch * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : '--:--';

const fmtDuration = mins => {
  if (mins === null || mins === undefined) return '—';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}min` : `${h}h`;
};

const slaColor = mins => {
  if (mins === null || mins === undefined) return '#94a3b8';
  if (mins <= 5) return '#10b981';
  if (mins <= 30) return '#f59e0b';
  return '#ef4444';
};
</script>

<template>
  <div
    class="flex flex-col h-full gap-3 p-4 border rounded-xl border-n-weak bg-n-surface-1"
    :class="{ 'opacity-60': !isRodizioEffective(agent) }"
  >
    <!-- Cabeçalho: avatar + nome/email/status + toggle rodízio -->
    <div class="flex items-start w-full gap-3">
      <div class="relative flex-shrink-0 rounded-full bg-n-slate-3 p-0.5">
        <img
          v-if="agent.thumbnail"
          :src="agent.thumbnail"
          :alt="agent.name"
          class="object-cover rounded-full size-10"
        />
        <div
          v-else
          class="flex items-center justify-center text-sm font-semibold rounded-full size-10 text-n-slate-11"
        >
          {{ initials(agent.available_name || agent.name) }}
        </div>
      </div>

      <div class="flex flex-col min-w-0 grow">
        <span class="text-base font-semibold truncate text-n-slate-12">
          {{ agent.available_name || agent.name }}
        </span>
        <span class="text-sm truncate text-n-slate-10">{{ agent.email }}</span>
        <span
          class="inline-flex items-center gap-1.5 mt-1 self-start px-2 py-0.5 text-xs font-medium rounded-full"
          :style="{
            color: cardColor(agent),
            backgroundColor: cardColor(agent) + '1f',
          }"
        >
          <span class="inline-block rounded-full size-1.5" :style="{ backgroundColor: cardColor(agent) }" />
          {{ cardLabel(agent) }}
        </span>
      </div>

      <div class="flex flex-col items-center flex-shrink-0 gap-0.5">
        <button
          type="button"
          role="switch"
          :aria-checked="isRodizioEffective(agent)"
          :aria-disabled="isAutomaticRodizioBlock(agent)"
          :disabled="isAutomaticRodizioBlock(agent)"
          :title="rodizioToggleTitle(agent)"
          class="relative inline-flex items-center w-9 h-5 p-0.5 overflow-hidden transition-colors rounded-full shrink-0 disabled:cursor-not-allowed disabled:opacity-70"
          :style="{ backgroundColor: isRodizioEffective(agent) ? '#10b981' : '#cbd5e1' }"
          @click="emit('toggle-rodizio')"
        >
          <span
            class="absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform duration-200 ease-out pointer-events-none"
            :style="{ transform: isRodizioEffective(agent) ? 'translateX(16px)' : 'translateX(0)' }"
          />
        </button>
        <span class="text-xs text-n-slate-10">Rodízio</span>
      </div>
    </div>

    <!-- Métricas do dia + pendentes/vencidos -->
    <div
      class="flex flex-wrap items-center justify-between gap-3 text-sm w-full"
    >
      <button
        v-if="(agent.received_leads || []).length || (agent.lost_leads || []).length || (agent.earlier_answered_leads || []).length"
        type="button"
        class="inline-flex items-center gap-1.5 px-0 py-0 font-semibold text-n-slate-12 transition-colors hover:text-n-slate-12"
        title="Leads que chegaram a ele hoje (API Oficial) — clique para ver"
        @click="emit('open-received-modal')"
      >
        <span class="i-lucide-inbox size-3.5" />
        {{ agent.received_today || 0 }} recebidos
        <span class="i-lucide-chevron-right size-3.5 text-n-slate-10" />
      </button>
      <button
        v-else
        type="button"
        class="inline-flex items-center gap-1.5 px-0 py-0 font-semibold text-n-slate-12 transition-colors hover:text-n-slate-12"
        title="Leads recebidos hoje — clique para ver"
        @click="emit('open-received-modal')"
      >
        <span class="i-lucide-inbox size-3.5" />
        0 recebidos
        <span class="i-lucide-chevron-right size-3.5 text-n-slate-10" />
      </button>
      <span
        class="inline-flex items-center gap-1.5 px-0 py-0 font-semibold text-n-slate-12"
        title="Conversas que ele respondeu hoje (API Oficial)"
      >
        <span class="i-lucide-message-circle-more size-3.5" />
        {{ agent.answered_today || 0 }} atendidos
      </span>
      <button
        v-if="pendingOf(agent).length"
        type="button"
        class="inline-flex items-center gap-1.5 px-0 py-0 font-semibold transition-colors hover:opacity-80"
        :style="{ color: badgeColor(agent) }"
        :title="commercial ? 'Leads sem resposta — clique para ver' : 'Fora do horário comercial: rodízio pausado'"
        @click="emit('toggle-expand')"
      >
        <span class="i-lucide-hourglass size-3.5" />
        {{ pendingOf(agent).length }} aguardando
        <span
          v-if="commercial && topLead(agent) && topLead(agent).circulating"
          class="tabular-nums"
        >
          {{ fmtCountdown(remainingFor(topLead(agent))) }}
        </span>
        <span v-else class="opacity-90">Aurora</span>
        <span class="text-[9px] leading-none">{{ isExpanded ? '▲' : '▼' }}</span>
      </button>
      <span
        v-if="vencidosOf(agent) > 0"
        class="inline-flex items-center gap-1.5 px-0 py-0 font-semibold text-[#b91c1c]"
        title="Leads que ele recebeu e não atendeu em 4 min (foram para outro vendedor)"
      >
        <span class="i-lucide-alert-triangle size-3.5" />
        {{ vencidosOf(agent) }} vencido{{ vencidosOf(agent) > 1 ? 's' : '' }}
      </span>
      <span
        v-if="!agent.rodizio_enabled"
        class="inline-flex items-center justify-center w-full gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11"
      >
        <span class="i-lucide-alert-triangle size-4" />
        Fora do rodízio
      </span>
      <span
        v-else-if="!isRodizioEffective(agent)"
        class="inline-flex items-center justify-center w-full gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border border-n-ruby-5 bg-n-ruby-3 text-n-ruby-11"
      >
        <span class="i-lucide-calendar-off size-4" />
        {{ agent.availability_label || 'Indisponível para o rodízio' }}
      </span>
      <span
        v-else-if="agent.availability_notice"
        class="inline-flex items-center justify-center w-full gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg border border-amber-200 bg-amber-50 text-amber-700"
      >
        <span class="i-lucide-info size-4" />
        {{ agent.availability_notice }}
      </span>
    </div>

    <!-- Lista expandida: leads de hoje + recebidos antes/atendidos hoje, com SLA -->
    <div
      v-if="isExpandedRecv && ((agent.received_leads || []).length || (agent.lost_leads || []).length || (agent.earlier_answered_leads || []).length)"
      class="flex flex-col w-full gap-2 pt-2 mt-2 border-t border-n-weak"
    >
      <div v-if="(agent.received_leads || []).length" class="flex flex-col gap-1">
        <span class="px-2 text-[11px] font-semibold tracking-wide uppercase text-n-slate-10">
          Recebidos hoje ({{ agent.received_leads.length }})
        </span>
        <a
          v-for="lead in agent.received_leads"
          :key="'rt-' + lead.display_id"
          :href="convUrl(lead.display_id)"
          class="flex items-center justify-between gap-3 px-2 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1"
        >
          <span class="truncate text-n-slate-12">
            {{ lead.contact_name }}
            <span class="text-n-slate-9">#{{ lead.display_id }}</span>
          </span>
          <span class="flex items-center flex-shrink-0 gap-2 text-[11px]">
            <span class="text-n-slate-10">
              lead {{ fmtHora(lead.first_inbound_at) }} → resp {{ fmtHora(lead.first_reply_at) }}
            </span>
            <span
              v-if="lead.answered"
              class="font-semibold"
              :style="{ color: slaColor(lead.response_minutes) }"
              title="Tempo entre a 1ª mensagem do lead e a 1ª resposta"
            >
              ⏱ {{ fmtDuration(lead.response_minutes) }}
            </span>
            <span v-else class="font-semibold text-amber-600" title="Ainda sem resposta">
              ⏳ sem resposta
            </span>
          </span>
        </a>
      </div>

      <div v-if="(agent.lost_leads || []).length" class="flex flex-col gap-1">
        <span class="px-2 text-[11px] font-semibold tracking-wide uppercase text-amber-600">
          Recebeu mas não atendeu em 4 min ({{ agent.lost_leads.length }})
        </span>
        <a
          v-for="lead in agent.lost_leads"
          :key="'rl-' + lead.display_id"
          :href="convUrl(lead.display_id)"
          class="flex items-center justify-between gap-3 px-2 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1"
        >
          <span class="truncate text-n-slate-12">
            {{ lead.contact_name }}
            <span class="text-n-slate-9">#{{ lead.display_id }}</span>
          </span>
          <span class="flex-shrink-0 text-[11px] text-amber-700">
            não atendeu → reatribuído p/
            <span class="font-semibold">{{ lead.reassigned_to }}</span>
          </span>
        </a>
      </div>

      <div v-if="(agent.earlier_answered_leads || []).length" class="flex flex-col gap-1">
        <span class="px-2 text-[11px] font-semibold tracking-wide uppercase text-n-slate-10">
          Recebidos antes · atendidos hoje ({{ agent.earlier_answered_leads.length }})
        </span>
        <a
          v-for="lead in agent.earlier_answered_leads"
          :key="'re-' + lead.display_id"
          :href="convUrl(lead.display_id)"
          class="flex items-center justify-between gap-3 px-2 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1"
        >
          <span class="truncate text-n-slate-12">
            {{ lead.contact_name }}
            <span class="text-n-slate-9">#{{ lead.display_id }}</span>
          </span>
          <span class="flex items-center flex-shrink-0 gap-2 text-[11px]">
            <span class="text-n-slate-10">
              lead {{ fmtHora(lead.first_inbound_at) }} → resp {{ fmtHora(lead.first_reply_at) }}
            </span>
            <span
              class="font-semibold"
              :style="{ color: slaColor(lead.response_minutes) }"
              title="Tempo entre a 1ª mensagem do lead e a 1ª resposta"
            >
              ⏱ {{ fmtDuration(lead.response_minutes) }}
            </span>
          </span>
        </a>
      </div>
    </div>

    <!-- Lista expandida: cada lead pendente com o próprio countdown -->
    <div
      v-if="isExpanded && pendingOf(agent).length"
      class="flex flex-col w-full gap-1 pt-2 mt-2 border-t border-n-weak"
    >
      <a
        v-for="lead in pendingOf(agent)"
        :key="lead.display_id"
        :href="convUrl(lead.display_id)"
        class="flex items-center justify-between gap-3 px-2 py-1.5 text-xs rounded-lg hover:bg-n-alpha-1"
      >
        <span class="truncate text-n-slate-12">
          {{ lead.contact_name }}
          <span class="text-n-slate-9">#{{ lead.display_id }}</span>
        </span>
        <span
          v-if="commercial && lead.circulating"
          class="flex-shrink-0 font-semibold tabular-nums"
          :style="{ color: urgencyColor(remainingFor(lead)) }"
        >
          {{ fmtCountdown(remainingFor(lead)) }}
          <span class="font-normal text-n-slate-10">p/ reatribuir</span>
        </span>
        <span v-else class="flex-shrink-0 text-n-slate-9">
          {{ commercial ? 'sem timer (>24h)' : 'noturno · Aurora' }}
        </span>
      </a>
    </div>

    <!-- Rodapé: vínculo do ID Salesforce (discreto, colapsável) -->
    <div class="pt-2 mt-auto border-t border-n-weak">
      <button
        v-if="!sfIsOpen"
        type="button"
        class="flex items-center gap-1.5 text-sm transition-colors text-n-slate-9 hover:text-n-slate-11"
        :title="agent.salesforce_id ? 'ID Salesforce vinculado — clique para editar' : 'Vincular ID do consultor no Salesforce'"
        @click="emit('toggle-sf')"
      >
        <span class="font-bold tracking-wider text-base">SF</span>
        <template v-if="agent.salesforce_id">
          <span class="truncate max-w-[150px] text-sm text-n-slate-10">{{ agent.salesforce_id }}</span>
          <span class="text-emerald-500">✓</span>
        </template>
        <span v-else class="text-sm underline decoration-dotted underline-offset-2">vincular Salesforce ID</span>
      </button>

      <div v-else class="flex items-center gap-1.5">
        <div class="flex items-center grow">
          <input
            :value="sfDraftValue"
            type="text"
            placeholder="ID Salesforce"
            spellcheck="false"
            class="w-full h-8 px-3 text-sm transition-colors border rounded-lg border-n-weak bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-[#6366f1]"
            @input="emit('update-sf-draft', $event.target.value)"
            @keydown.enter.prevent="emit('save-salesforce-id')"
          />
        </div>
        <button
          v-if="(sfDraftValue || '') !== (agent.salesforce_id || '')"
          type="button"
          :disabled="sfIsBusy"
          class="flex-shrink-0 h-8 px-3 text-sm font-semibold text-white transition-opacity rounded-lg disabled:opacity-40 hover:brightness-110"
          style="background-color: #6366f1"
          @click="emit('save-salesforce-id')"
        >
          {{ sfIsBusy ? 'Salvando…' : 'Salvar' }}
        </button>
        <button
          type="button"
          class="flex-shrink-0 px-1.5 text-base leading-none text-n-slate-9 hover:text-n-slate-11"
          title="Fechar"
          @click="emit('toggle-sf')"
        >
          ✕
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.aceleres-pulse {
  animation: aceleres-pulse 1.8s ease-in-out infinite;
}
@keyframes aceleres-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}
</style>
